import { Alert, Button, Card, Checkbox, Col, ConfigProvider, DatePicker, Divider, Form, Input, InputNumber, Row, Select, Statistic, Typography } from "antd";
import SDIModal from "../../../../components/ui/SDIModal";
import PropTypes from "prop-types";
import frFR from "antd/locale/fr_FR";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axiosInstance from "../../../../variables/api/axiosInstance";
import AvenantAgencySelector from "./AvenantAgencySelector";

const { Text, Title } = Typography;
const { Option } = Select;

const normalizeConducteurHabituelPayload = (conducteur) => {
    if (!conducteur) return null;
    const nom = (conducteur.nom || "").trim();
    const prenom = (conducteur.prenom || "").trim();
    const adresse = (conducteur.adresse || "").trim();
    const telephone = (conducteur.telephone || "").replace(/\s/g, "");

    if (!nom || !prenom || !adresse || !telephone) {
        return null;
    }

    const { telephone: _rawTelephone, contact = [], ...rest } = conducteur;
    const contacts = telephone
        ? [telephone]
        : Array.isArray(contact)
            ? contact.filter(Boolean)
            : [];

    return {
        ...rest,
        contact: contacts,
    };
};

const Incorporation = (props) => {
    const [modalIncorporation, setModalIncorporation] = useState(props.open);
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();

    // États pour les données du contrat et de l'incorporation
    const [contratFlotte, setContratFlotte] = useState(null);
    const [dateEffetIncorporation, setDateEffetIncorporation] = useState(dayjs());
    const [joursRestants, setJoursRestants] = useState(0);
    const [preview, setPreview] = useState(null);
    const [fraisAccessoires, setFraisAccessoires] = useState(0);
    const [valeurVenaleMinimale, setValeurVenaleMinimale] = useState(0);

    // États pour les engins à incorporer
    const [enginsData, setEnginsData] = useState([{
        // Informations engin
        categorie: 'MOTO',
        matricule: '',
        typeSerie: 'TG',
        numeroSerie: '',
        marque: '',
        energie: 'ESSENCE',
        volume: '',
        nombrePlace: '',
        usage: 'PRIVE',
        moteur: '',
        type: '',
        genre: 'VELOMOTEUR',
        numeroChassis: '',
        valeurVenale: '',
        valeurANeuf: '',
        valeurDeclaree: '',
        dateMiseEnCirculation: null,
        carteGrise: null,

        // Conducteur habituel (optionnel)
        conducteurHabituel: null,
        hasConducteurDifferent: false,

        // Garanties
        garantiesSupplementaires: []
    }]);

    // Modal pour le conducteur habituel
    const [modalConducteur, setModalConducteur] = useState({
        show: false,
        enginIndex: null,
        data: {
            nom: '',
            prenom: '',
            genre: 'MASCULIN',
            profession: '',
            email: '',
            adresse: '',
            telephone: '',
            typePiece: '',
            numIdentite: '',
            dateDelivrance: ''
        }
    });

    // Données de référence
    const [marques, setMarques] = useState([]);
    const [garantiesNonObligatoires, setGarantiesNonObligatoires] = useState([]);
    const [professions, setProfessions] = useState([]);

    useEffect(() => {
        setModalIncorporation(props.open);
        if (props.open && props?.contrat?.codeId) {
            console.log("Contrat flotte pour incorporation:", props?.contrat);
            setContratFlotte(props?.contrat);

            // Calculer les jours restants jusqu'à l'échéance
            const dateEcheance = dayjs(props?.contrat?.dateEcheance);
            const jours = dateEcheance.diff((dateEffetIncorporation || dayjs()), 'day');
            setJoursRestants(jours);

            // Charger les données de référence
            loadReferenceData(props?.contrat);
        }
    }, [props.open, props?.contrat?.codeId]);

    const { PlusCircleOutlined, UserOutlined, CarOutlined, CalendarOutlined, DollarOutlined, InfoCircleOutlined, CheckCircleOutlined, WarningOutlined, DeleteOutlined, CreditCardOutlined } = require('@ant-design/icons');

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XAF' }).format(value);
    };

    const getValeurVenaleMinimum = (engin) => {
        const categorieMin = engin?.categorie && typeof engin.categorie === 'object'
            ? engin.categorie?.valeurVenaleMinimale
            : undefined;

        if (categorieMin !== undefined && categorieMin !== null) {
            const numericCategorieMin = Number(categorieMin);
            if (!Number.isNaN(numericCategorieMin)) {
                return numericCategorieMin;
            }
        }

        const contratCategorieMin = props?.contrat?.engins?.[0]?.categorie?.valeurVenaleMinimale;
        if (contratCategorieMin !== undefined && contratCategorieMin !== null) {
            const numericContratCategorieMin = Number(contratCategorieMin);
            if (!Number.isNaN(numericContratCategorieMin)) {
                return numericContratCategorieMin;
            }
        }

        if (valeurVenaleMinimale !== undefined && valeurVenaleMinimale !== null) {
            const numericValeurVenaleMinimale = Number(valeurVenaleMinimale);
            if (!Number.isNaN(numericValeurVenaleMinimale)) {
                return numericValeurVenaleMinimale;
            }
        }

        return 0;
    };

    const loadReferenceData = async (contrat) => {
        try {
            // Charger les marques pour motos
            const marquesRes = await axiosInstance.get('/api/parametre/type/MARQUE');
            setMarques(marquesRes.data.filter((el) => el.categorie?.nom?.trim() === "MOTO"));

            // Charger les garanties non obligatoires
            const garantiesRes = await axiosInstance.get(`/api/garantie/categorie/non-compulsory/${props?.contrat?.engins[0].categorie.categorieId}/${props?.contrat?.organisme?.code}`);
            setGarantiesNonObligatoires(garantiesRes.data);

            // Charger les professions
            const professionsRes = await axiosInstance.get('/api/parametre/type/PROFESSION');
            setProfessions(professionsRes.data);

            // Charger les frais accessoires
            const fraisRes = await axiosInstance.get(`/api/avenant/accessoires/IN/${props?.contrat?.engins[0]?.categorie?.codeCategorie}`);
            setFraisAccessoires(fraisRes.data);

            const rawValeurMin = contrat?.engins[0]?.categorie?.valeurVenaleMinimale ?? contrat?.engins[0]?.valeurVenale ?? 0;
            const valeurMin = Number(rawValeurMin) || 0;

            setValeurVenaleMinimale(valeurMin);

            // Initialiser la valeur vénale minimale pour le premier engin
            setEnginsData(prev => {
                const newData = [...prev];
                const engin = newData[0];

                if (!engin) {
                    return prev;
                }

                const shouldUpdateField = (fieldValue) => {
                    if (fieldValue === null || fieldValue === undefined || fieldValue === '') {
                        return true;
                    }
                    const numericValue = Number(fieldValue);
                    if (Number.isNaN(numericValue)) {
                        return true;
                    }
                    return numericValue < valeurMin;
                };

                const updatedEngin = { ...engin };
                let hasChanged = false;

                if (shouldUpdateField(updatedEngin.valeurVenale)) {
                    updatedEngin.valeurVenale = valeurMin;
                    hasChanged = true;
                }
                if (shouldUpdateField(updatedEngin.valeurANeuf)) {
                    updatedEngin.valeurANeuf = valeurMin;
                    hasChanged = true;
                }
                if (shouldUpdateField(updatedEngin.valeurDeclaree)) {
                    updatedEngin.valeurDeclaree = valeurMin;
                    hasChanged = true;
                }

                if (!hasChanged) {
                    return prev;
                }

                newData[0] = updatedEngin;
                return newData;
            });
        } catch (error) {
            console.error("Erreur lors du chargement des données:", error);
        }
    };

    // Gestion des engins
    const handleEnginChange = (value, field, index) => {
        setEnginsData(prev => {
            const newData = [...prev];
            const existingEngin = newData[index];

            if (!existingEngin) {
                return prev;
            }

            const updatedEngin = { ...existingEngin };

            // Formater en majuscules pour les champs texte
            let processedValue = value;
            if (typeof value === 'string' && ['matricule', 'numeroSerie', 'marque', 'moteur', 'type', 'numeroChassis'].includes(field)) {
                processedValue = value.toUpperCase();
            }

            // Gestion spéciale pour la valeur vénale
            if (field === 'valeurVenale') {
                const isEmpty = processedValue === null || processedValue === undefined || processedValue === '';
                const oldValeurVenale = updatedEngin.valeurVenale;
                const numericValue = typeof processedValue === 'number' ? processedValue : Number(processedValue);
                const hasNumericValue = !Number.isNaN(numericValue);
                const valeurCourante = isEmpty || !hasNumericValue ? null : numericValue;

                updatedEngin.valeurVenale = valeurCourante;

                if (!isEmpty && hasNumericValue) {
                    const shouldSyncRelatedValue = (fieldValue) => {
                        if (fieldValue === null || fieldValue === undefined || fieldValue === '') {
                            return true;
                        }
                        const numericFieldValue = Number(fieldValue);
                        if (Number.isNaN(numericFieldValue)) {
                            return true;
                        }
                        if (oldValeurVenale === null || oldValeurVenale === undefined || oldValeurVenale === '') {
                            return true;
                        }
                        const numericOld = Number(oldValeurVenale);
                        if (Number.isNaN(numericOld)) {
                            return true;
                        }
                        return numericFieldValue === numericOld;
                    };

                    if (shouldSyncRelatedValue(updatedEngin.valeurANeuf)) {
                        updatedEngin.valeurANeuf = numericValue;
                    }
                    if (shouldSyncRelatedValue(updatedEngin.valeurDeclaree)) {
                        updatedEngin.valeurDeclaree = numericValue;
                    }
                }

                newData[index] = updatedEngin;
                return newData;
            }

            updatedEngin[field] = processedValue;
            newData[index] = updatedEngin;
            return newData;
        });
    };

    const handleValeurVenaleBlur = (index) => {
        setEnginsData(prev => {
            const newData = [...prev];
            const engin = newData[index];

            if (!engin) {
                return prev;
            }

            const minValeur = getValeurVenaleMinimum(engin);
            const isEmpty = engin.valeurVenale === null || engin.valeurVenale === undefined || engin.valeurVenale === '';
            const valeurNumerique = Number(engin.valeurVenale);
            const valeurValide = !Number.isNaN(valeurNumerique);
            const needsAdjustment = isEmpty || !valeurValide || valeurNumerique < minValeur;

            if (!needsAdjustment) {
                return prev;
            }

            toast.warning(`La valeur vénale ne peut être inférieure à ${formatCurrency(minValeur)}`);

            const shouldUpdateRelatedValue = (fieldValue) => {
                if (fieldValue === null || fieldValue === undefined || fieldValue === '') {
                    return true;
                }
                const numericFieldValue = Number(fieldValue);
                if (Number.isNaN(numericFieldValue)) {
                    return true;
                }
                if (valeurValide && numericFieldValue === valeurNumerique) {
                    return true;
                }
                return numericFieldValue < minValeur;
            };

            const updatedEngin = {
                ...engin,
                valeurVenale: minValeur
            };

            if (shouldUpdateRelatedValue(engin.valeurANeuf)) {
                updatedEngin.valeurANeuf = minValeur;
            }
            if (shouldUpdateRelatedValue(engin.valeurDeclaree)) {
                updatedEngin.valeurDeclaree = minValeur;
            }

            newData[index] = updatedEngin;
            return newData;
        });
    };

    const addEngin = () => {
        const valeurMinimale = getValeurVenaleMinimum();
        setEnginsData(prev => [...prev, {
            categorie: 'MOTO',
            matricule: '',
            typeSerie: 'TG',
            numeroSerie: '',
            marque: '',
            energie: 'ESSENCE',
            volume: '',
            nombrePlace: '',
            usage: 'PRIVE',
            moteur: '',
            type: '',
            genre: 'VELOMOTEUR',
            numeroChassis: '',
            valeurVenale: valeurMinimale,
            valeurANeuf: valeurMinimale,
            valeurDeclaree: valeurMinimale,
            dateMiseEnCirculation: null,
            carteGrise: null,
            conducteurHabituel: null,
            hasConducteurDifferent: false,
            garantiesSupplementaires: []
        }]);
    };

    const removeEngin = (index) => {
        if (enginsData.length === 1) {
            toast.error("Vous devez avoir au moins un engin à incorporer");
            return;
        }
        setEnginsData(prev => prev.filter((_, i) => i !== index));
    };

    // Gestion du conducteur habituel
    const handleShowConducteurModal = (enginIndex) => {
        const conducteurExistant = enginsData[enginIndex].conducteurHabituel;

        setModalConducteur({
            show: true,
            enginIndex: enginIndex,
            data: conducteurExistant || {
                nom: '',
                prenom: '',
                genre: 'MASCULIN',
                profession: '',
                email: '',
                adresse: '',
                telephone: '',
                typePiece: '',
                numIdentite: '',
                dateDelivrance: ''
            }
        });
    };

    const handleConducteurChange = (e) => {
        const { name, value } = e.target;

        // Formater en majuscules pour les champs texte pertinents
        let processedValue = value;
        if (typeof value === 'string' && ['nom', 'prenom', 'adresse', 'numIdentite'].includes(name)) {
            processedValue = value.toUpperCase();
        }

        setModalConducteur(prev => ({
            ...prev,
            data: {
                ...prev.data,
                [name]: processedValue
            }
        }));
    };

    const saveConducteurHabituel = () => {
        const { enginIndex, data } = modalConducteur;

        if (!data.nom || !data.prenom || !data.adresse || !data.telephone) {
            toast.error('Veuillez remplir tous les champs obligatoires');
            return;
        }

        setEnginsData(prev => {
            const newData = [...prev];
            newData[enginIndex] = {
                ...newData[enginIndex],
                conducteurHabituel: data,
                hasConducteurDifferent: true
            };
            return newData;
        });

        setModalConducteur({
            show: false,
            enginIndex: null,
            data: {
                nom: '',
                prenom: '',
                genre: 'MASCULIN',
                profession: '',
                email: '',
                adresse: '',
                telephone: '',
                typePiece: '',
                numIdentite: '',
                dateDelivrance: ''
            }
        });

        toast.success('Conducteur habituel enregistré');
    };

    const removeConducteurHabituel = (enginIndex) => {
        setEnginsData(prev => {
            const newData = [...prev];
            newData[enginIndex] = {
                ...newData[enginIndex],
                conducteurHabituel: null,
                hasConducteurDifferent: false
            };
            return newData;
        });
        toast.info('Conducteur habituel retiré - le souscripteur sera utilisé par défaut');
    };

    // Calcul du preview
    const calculatePreview = async () => {
        if (!contratFlotte || enginsData.length === 0) return;

        try {
            const payload = {
                codeId: contratFlotte.codeId,
                dateEffet: dateEffetIncorporation.format('YYYY-MM-DDTHH:mm:ss'),
                engins: enginsData.map(engin => {
                    const conducteurHabituel = normalizeConducteurHabituelPayload(engin.conducteurHabituel);
                    const useSouscripteur = !conducteurHabituel;
                    return {
                        ...engin,
                        conducteurHabituel,
                        hasConducteurDifferent: !useSouscripteur,
                        puissance: engin.volume,
                        immatriculation: engin.typeSerie + engin.numeroSerie + engin.matricule,
                        categorie: props?.contrat?.engins[0].categorie,
                        dateMiseEnCirculation: engin.dateMiseEnCirculation?.format('YYYY-MM-DD'),
                        useSouscripteur
                    };
                })
            };
            console.log("Payload incorporation:", payload);
            if (!isValidatedEngins()) return;
            const response = await axiosInstance.put(`/api/engin/preview-incorporation`, JSON.stringify(payload));
            console.log("Preview incorporation:", response.data);
            setPreview(response.data);
        } catch (error) {
            console.error("Erreur lors du calcul du preview:", error);
        }
    };

    useEffect(() => {
        if (dateEffetIncorporation && enginsData.length > 0) {
            // Calculer les jours restants jusqu'à l'échéance
            const dateEcheance = dayjs(props?.contrat?.dateEcheance);
            const jours = dateEcheance.diff((dateEffetIncorporation || dayjs()), 'day');
            setJoursRestants(jours);

            const timeoutId = setTimeout(() => calculatePreview(), 500);
            return () => clearTimeout(timeoutId);
        }
    }, [dateEffetIncorporation, enginsData, contratFlotte]);

    const navigate = useNavigate();

    const enterLoading = () => setLoading(true);
    const outLoading = () => setLoading(false);

    const validateEngins = () => {
        for (let i = 0; i < enginsData.length; i++) {
            const engin = enginsData[i];
            const valeurVenaleManquante = engin.valeurVenale === null || engin.valeurVenale === undefined || engin.valeurVenale === '';
            if (!engin.matricule || !engin.marque || !engin.numeroChassis ||
                !engin.moteur || !engin.type || valeurVenaleManquante ||
                !engin.dateMiseEnCirculation) {
                toast.error(`Veuillez remplir tous les champs obligatoires de l'engin N°${i + 1}`);
                return false;
            }
            const minValeur = getValeurVenaleMinimum(engin);
            const valeurNumerique = Number(engin.valeurVenale);
            if (Number.isNaN(valeurNumerique) || valeurNumerique < minValeur) {
                toast.error(`La valeur vénale de l'engin N°${i + 1} doit être supérieure ou égale à ${formatCurrency(minValeur)}`);
                return false;
            }
        }
        return true;
    };

    const isValidatedEngins = () => {
        for (let i = 0; i < enginsData.length; i++) {
            const engin = enginsData[i];
            const valeurVenaleManquante = engin.valeurVenale === null || engin.valeurVenale === undefined || engin.valeurVenale === '';
            if (!engin.matricule || !engin.marque || !engin.numeroChassis ||
                !engin.moteur || !engin.type || valeurVenaleManquante ||
                !engin.dateMiseEnCirculation) {
                return false;
            }
            const minValeur = getValeurVenaleMinimum(engin);
            const valeurNumerique = Number(engin.valeurVenale);
            if (Number.isNaN(valeurNumerique) || valeurNumerique < minValeur) {
                return false;
            }
        }
        return true;
    };

    const handleIncorporationSubmit = async () => {
        try {
            await form.validateFields();

            if (!validateEngins()) return;

            if (!isContratEligible()) {
                toast.error("Ce contrat n'est pas éligible à l'incorporation");
                return;
            }

            enterLoading();

            const payload = {
                codeId: contratFlotte.codeId,
                //Format dateEffet so that it can be accepted by spring boot a LocalDateTime
                dateEffet: dateEffetIncorporation.format('YYYY-MM-DDTHH:mm:ss'),
                engins: enginsData.map(engin => {
                    const conducteurHabituel = normalizeConducteurHabituelPayload(engin.conducteurHabituel);
                    const useSouscripteur = !conducteurHabituel;
                    return {
                        ...engin,
                        conducteurHabituel,
                        hasConducteurDifferent: !useSouscripteur,
                        puissance: engin.volume,
                        immatriculation: engin.typeSerie + engin.numeroSerie + engin.matricule,
                        categorie: props?.contrat?.engins[0].categorie,
                        dateMiseEnCirculation: engin.dateMiseEnCirculation?.format('YYYY-MM-DD'),
                        useSouscripteur
                    };
                }),
                primeNette: preview?.primeNette || 0,
                accessoires: fraisAccessoires,
                taxes: preview?.taxes || 0,
                primeTotale: preview?.primeTotale || 0
            };

            console.log("Payload incorporation:", payload);

            const response = await axiosInstance.put(
                `/api/engin/incorporation`,
                JSON.stringify(payload)
            );

            toast.success("Incorporation effectuée avec succès!");
            setModalIncorporation(false);
            props.update(false);
            navigate(`/details-contrat/${response.data.codeId}`, { replace: true });
            window.location.reload();

        } catch (error) {
            console.error(error);
            toast.error("Erreur lors de l'incorporation");
        } finally {
            outLoading();
        }
    };

    const handleCancel = () => {
        setModalIncorporation(false);
        props.update(false);
    };

    const isContratEligible = () => {
        return contratFlotte?.engins && contratFlotte?.engins?.length &&
            joursRestants > 0;
    };

    return (
        <>
            <SDIModal
                title="INCORPORATION D'ENGINS"
                centered
                confirmLoading={loading}
                open={modalIncorporation}
                onOk={handleIncorporationSubmit}
                onCancel={handleCancel}
                width={1200}
                zIndex={1940}
                okText="Incorporer les engins"
                cancelText="Annuler"
            >
                <ConfigProvider locale={frFR}>
                    <div className="incorporation-content">
                        <AvenantAgencySelector
                            organismeCode={props?.contrat?.organisme?.code}
                            defaultAgence={props?.contrat?.producteur?.agence || props?.contrat?.intermediaire?.agence}
                        />

                        {/* Informations sur le contrat flotte */}
                        {contratFlotte && (
                            <Card className="mb-4" size="small" title="Contrat flotte">
                                <Row gutter={16}>
                                    <Col span={8}>
                                        <Statistic
                                            title="Numéro de police"
                                            value={contratFlotte.numeroPoliceP || contratFlotte.numeroPoliceM}
                                        />
                                    </Col>
                                    <Col span={8}>
                                        <Statistic
                                            title="Jours restants"
                                            value={`${joursRestants} jours`}
                                            valueStyle={{ color: joursRestants > 0 ? '#3f8600' : '#cf1322' }}
                                            prefix={<CalendarOutlined />}
                                        />
                                    </Col>
                                    <Col span={8}>
                                        <Statistic
                                            title="Date d'échéance"
                                            value={dayjs(contratFlotte.dateEcheance).format('DD/MM/YYYY')}
                                        />
                                    </Col>
                                </Row>
                            </Card>
                        )}

                        {/* Conditions d'éligibilité */}
                        {!isContratEligible() ? (
                            <Alert
                                message="Contrat non éligible"
                                description="L'incorporation n'est possible que sur les contrats flotte actifs avec des jours restants."
                                type="error"
                                icon={<WarningOutlined />}
                                showIcon
                                className="mb-4"
                            />
                        ) : null}

                        {/* Règles d'incorporation */}
                        {/* <Card className="mb-4" size="small" title="Règles d'incorporation">
                            <div className="info-section">
                                <div className="info-item">
                                    <InfoCircleOutlined className="info-icon info" />
                                    <Text>
                                        <Text strong>Code avenant :</Text> AI + Année + 001
                                    </Text>
                                </div>
                                <div className="info-item">
                                    <CarOutlined className="info-icon warning" />
                                    <Text>
                                        <Text strong>Catégorie :</Text> Uniquement engins à 2 ou 3 roues
                                    </Text>
                                </div>
                                <div className="info-item">
                                    <CalendarOutlined className="info-icon success" />
                                    <Text>
                                        <Text strong>Prime :</Text> Calculée au prorata des jours restants
                                    </Text>
                                </div>
                            </div>
                        </Card> */}

                        <Form form={form} layout="vertical">
                            {/* Date d'effet */}
                            <Form.Item
                                label="Date d'effet de l'incorporation"
                                name="dateEffet"
                                rules={[{ required: true, message: 'Veuillez sélectionner une date' }]}
                                initialValue={dateEffetIncorporation}
                            >
                                <DatePicker
                                    style={{ width: '100%' }}
                                    format="DD/MM/YYYY"
                                    value={dateEffetIncorporation}
                                    onChange={setDateEffetIncorporation}
                                    disabledDate={(current) => {
                                        const dateEcheance = contratFlotte ? dayjs(contratFlotte.dateEcheance) : dayjs().add(1, 'year');
                                        return current && (
                                            current < dayjs().startOf('day') ||
                                            current > dateEcheance
                                        );
                                    }}
                                />
                            </Form.Item>

                            {/* Liste des engins à incorporer */}
                            <Title level={5} className="mb-3">Engins à incorporer</Title>

                            {enginsData.map((engin, index) => (
                                <Card
                                    key={index}
                                    className="mb-3"
                                    size="small"
                                    title={`Engin N°${index + 1}`}
                                    extra={
                                        enginsData.length > 1 && (
                                            <Button
                                                danger
                                                size="small"
                                                icon={<DeleteOutlined />}
                                                onClick={() => removeEngin(index)}
                                            >
                                                Supprimer
                                            </Button>
                                        )
                                    }
                                >
                                    <Row gutter={16}>
                                        {/* Immatriculation */}
                                        <Col span={8}>
                                            <Form.Item label="Immatriculation *">
                                                <Input.Group compact>
                                                    <Select
                                                        value={engin.typeSerie}
                                                        onChange={(val) => handleEnginChange(val, 'typeSerie', index)}
                                                        style={{ width: '20%' }}
                                                    >
                                                        <Option value="TG">TG</Option>
                                                        <Option value="Autre">Autre</Option>
                                                    </Select>
                                                    <Input
                                                        value={engin.matricule}
                                                        onChange={(e) => handleEnginChange(e.target.value, 'matricule', index)}
                                                        style={{ width: '50%' }}
                                                        placeholder="Immatriculation"
                                                    />
                                                    {engin.typeSerie === 'TG' && (
                                                        <Input
                                                            value={engin.numeroSerie}
                                                            onChange={(e) => handleEnginChange(e.target.value, 'numeroSerie', index)}
                                                            style={{ width: '30%' }}
                                                            placeholder="N° série"
                                                        />
                                                    )}
                                                </Input.Group>
                                            </Form.Item>
                                        </Col>

                                        {/* Marque */}
                                        <Col span={8}>
                                            <Form.Item label="Marque *">
                                                <Select
                                                    value={engin.marque}
                                                    onChange={(val) => handleEnginChange(val, 'marque', index)}
                                                    placeholder="Sélectionner la marque"
                                                    showSearch
                                                >
                                                    {marques.map((mar, id) => (
                                                        <Option key={id} value={mar.libelleParametre}>
                                                            {mar.libelleParametre}
                                                        </Option>
                                                    ))}
                                                </Select>
                                            </Form.Item>
                                        </Col>

                                        {/* Énergie */}
                                        <Col span={8}>
                                            <Form.Item label="Énergie *">
                                                <Select
                                                    value={engin.energie}
                                                    onChange={(val) => handleEnginChange(val, 'energie', index)}
                                                >
                                                    <Option value="ESSENCE">ESSENCE</Option>
                                                    <Option value="DIESEL">DIESEL</Option>
                                                    <Option value="ELECTRIQUE">ÉLECTRIQUE</Option>
                                                </Select>
                                            </Form.Item>
                                        </Col>

                                        {/* Usage */}
                                        <Col span={8}>
                                            <Form.Item label="Usage *">
                                                <Select
                                                    value={engin.usage}
                                                    onChange={(val) => handleEnginChange(val, 'usage', index)}
                                                >
                                                    <Option value="PRIVE">PRIVE</Option>
                                                    <Option value="TAXI">TAXI</Option>
                                                </Select>
                                            </Form.Item>
                                        </Col>

                                        {/* Volume */}
                                        <Col span={8}>
                                            <Form.Item label="Volume (cm³) *">
                                                <InputNumber
                                                    value={engin.volume}
                                                    onChange={(val) => handleEnginChange(val, 'volume', index)}
                                                    style={{ width: '100%' }}
                                                    placeholder="Volume"
                                                />
                                            </Form.Item>
                                        </Col>

                                        {/* Nombre de places */}
                                        <Col span={8}>
                                            <Form.Item label="Nombre de places *">
                                                <InputNumber
                                                    value={engin.nombrePlace}
                                                    onChange={(val) => handleEnginChange(val, 'nombrePlace', index)}
                                                    style={{ width: '100%' }}
                                                    placeholder="Nombre de places"
                                                />
                                            </Form.Item>
                                        </Col>

                                        {/* Genre */}
                                        <Col span={8}>
                                            <Form.Item label="Genre *">
                                                <Select
                                                    value={engin.genre}
                                                    onChange={(val) => handleEnginChange(val, 'genre', index)}
                                                >
                                                    <Option value="VELOMOTEUR">Vélomoteur</Option>
                                                    <Option value="TRICYCLE">Tricycle</Option>
                                                    <Option value="MOTOCYCLETTE">Motocyclette</Option>
                                                    <Option value="CYCLOMOTEUR">Cyclomoteur</Option>
                                                </Select>
                                            </Form.Item>
                                        </Col>

                                        {/* N° Moteur */}
                                        <Col span={8}>
                                            <Form.Item label="N° Moteur *">
                                                <Input
                                                    value={engin.moteur}
                                                    onChange={(e) => handleEnginChange(e.target.value, 'moteur', index)}
                                                    placeholder="Numéro de moteur"
                                                />
                                            </Form.Item>
                                        </Col>

                                        {/* Type */}
                                        <Col span={8}>
                                            <Form.Item label="Type *">
                                                <Input
                                                    value={engin.type}
                                                    onChange={(e) => handleEnginChange(e.target.value, 'type', index)}
                                                    placeholder="Type"
                                                />
                                            </Form.Item>
                                        </Col>

                                        {/* N° Châssis */}
                                        <Col span={8}>
                                            <Form.Item label="N° Châssis *">
                                                <Input
                                                    value={engin.numeroChassis}
                                                    onChange={(e) => handleEnginChange(e.target.value, 'numeroChassis', index)}
                                                    placeholder="Numéro de châssis"
                                                />
                                            </Form.Item>
                                        </Col>

                                        {/* Valeur vénale */}
                                        <Col span={8}>
                                            <Form.Item label="Valeur vénale *">
                                                <InputNumber
                                                    value={engin.valeurVenale}
                                                    onChange={(val) => handleEnginChange(val, 'valeurVenale', index)}
                                                    onBlur={() => handleValeurVenaleBlur(index)}
                                                    style={{ width: '100%' }}
                                                    formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                                                    parser={(value) => value.replace(/\s/g, '')}
                                                    placeholder="Valeur vénale"
                                                />
                                            </Form.Item>
                                        </Col>

                                        {/* Valeur déclarée */}
                                        <Col span={8}>
                                            <Form.Item label="Valeur déclarée *">
                                                <InputNumber
                                                    value={engin.valeurDeclaree}
                                                    onChange={(val) => handleEnginChange(val, 'valeurDeclaree', index)}
                                                    style={{ width: '100%' }}
                                                    formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                                                    parser={(value) => value.replace(/\s/g, '')}
                                                    placeholder="Valeur Déclarée"
                                                />
                                            </Form.Item>
                                        </Col>

                                        {/* Valeur à neuf */}
                                        <Col span={8}>
                                            <Form.Item label="Valeur à neuf *">
                                                <InputNumber
                                                    value={engin.valeurANeuf}
                                                    onChange={(val) => handleEnginChange(val, 'valeurANeuf', index)}
                                                    style={{ width: '100%' }}
                                                    formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                                                    parser={(value) => value.replace(/\s/g, '')}
                                                    placeholder="Valeur à neuf"
                                                />
                                            </Form.Item>
                                        </Col>

                                        {/* Date de mise en circulation */}
                                        <Col span={8}>
                                            <Form.Item label="Année de mise en circulation *">
                                                <DatePicker
                                                    value={engin.dateMiseEnCirculation}
                                                    onChange={(date) => handleEnginChange(date, 'dateMiseEnCirculation', index)}
                                                    picker="year"
                                                    style={{ width: '100%' }}
                                                    placeholder="Sélectionner l'année"
                                                    disabledDate={(current) => current && current > dayjs()}
                                                />
                                            </Form.Item>
                                        </Col>

                                        {/* Garanties supplémentaires */}
                                        <Col span={8}>
                                            <Form.Item label="Garanties supplémentaires">
                                                <Select
                                                    mode="multiple"
                                                    value={engin.garantiesSupplementaires}
                                                    onChange={(val) => handleEnginChange(val, 'garantiesSupplementaires', index)}
                                                    placeholder="Sélectionner les garanties"
                                                >
                                                    {garantiesNonObligatoires.map((gar, id) => (
                                                        <Option key={id} value={gar.codeId}>
                                                            {gar.nom}
                                                        </Option>
                                                    ))}
                                                </Select>
                                            </Form.Item>
                                        </Col>

                                        {/* Conducteur habituel */}
                                        <Col span={24}>
                                            {engin.hasConducteurDifferent ? (
                                                <Card size="small" className="bg-light">
                                                    <Row justify="space-between" align="middle">
                                                        <Col>
                                                            <Text strong>Conducteur habituel : </Text>
                                                            <Text>
                                                                {engin.conducteurHabituel?.nom} {engin.conducteurHabituel?.prenom}
                                                            </Text>
                                                        </Col>
                                                        <Col>
                                                            <Button
                                                                size="small"
                                                                onClick={() => handleShowConducteurModal(index)}
                                                                className="me-2"
                                                            >
                                                                Modifier
                                                            </Button>
                                                            <Button
                                                                size="small"
                                                                danger
                                                                onClick={() => removeConducteurHabituel(index)}
                                                            >
                                                                Retirer
                                                            </Button>
                                                        </Col>
                                                    </Row>
                                                </Card>
                                            ) : (
                                                <Alert
                                                    message="Par défaut, le souscripteur du contrat sera le conducteur habituel"
                                                    type="info"
                                                    action={
                                                        <Button
                                                            size="small"
                                                            type="primary"
                                                            icon={<UserOutlined />}
                                                            onClick={() => handleShowConducteurModal(index)}
                                                        >
                                                            Préciser un conducteur différent
                                                        </Button>
                                                    }
                                                />
                                            )}
                                        </Col>
                                    </Row>
                                </Card>
                            ))}

                            {/* Bouton pour ajouter un engin */}
                            <Button
                                type="dashed"
                                onClick={addEngin}
                                icon={<PlusCircleOutlined />}
                                block
                                className="mb-4"
                            >
                                Ajouter un autre engin
                            </Button>

                            {/* Récapitulatif financier */}
                            {preview && (
                                <Card className="cost-summary" size="small">
                                    <Title level={5}>
                                        <DollarOutlined className="mr-2" />
                                        Récapitulatif financier
                                    </Title>
                                    <div className="cost-breakdown">
                                        <Row justify="space-between" className="cost-line">
                                            <Col>
                                                <Text>Prime nette au prorata ({joursRestants} jours):</Text>
                                            </Col>
                                            <Col>
                                                <Text strong>{formatCurrency(preview.primeNette || 0)}</Text>
                                            </Col>
                                        </Row>

                                        <Row justify="space-between" className="cost-line">
                                            <Col>
                                                <Text>Frais accessoires:</Text>
                                            </Col>
                                            <Col>
                                                <Text>{formatCurrency(preview.accessoires || 0)}</Text>
                                            </Col>
                                        </Row>

                                        <Row justify="space-between" className="cost-line">
                                            <Col>
                                                <Text>Taxes (6%):</Text>
                                            </Col>
                                            <Col>
                                                <Text>{formatCurrency(preview.taxes || 0)}</Text>
                                            </Col>
                                        </Row>

                                        <Divider className="my-2" />

                                        <Row justify="space-between" className="cost-line total-cost">
                                            <Col>
                                                <Text strong className="text-lg">PRIME TOTALE:</Text>
                                            </Col>
                                            <Col>
                                                <Text strong className="text-lg text-blue-600">
                                                    {formatCurrency(preview.primeTotale || 0)}
                                                </Text>
                                            </Col>
                                        </Row>
                                    </div>
                                </Card>
                            )}
                        </Form>
                    </div>
                </ConfigProvider>

                <style jsx>
                    {`
                        .incorporation-content {
                            max-height: 70vh;
                            overflow-y: auto;
                        }
                        
                        .info-section {
                            display: flex;
                            flex-direction: column;
                            gap: 12px;
                        }
                        
                        .info-item {
                            display: flex;
                            align-items: flex-start;
                            gap: 8px;
                        }
                        
                        .info-icon {
                            margin-top: 2px;
                            font-size: 16px;
                        }
                        
                        .info-icon.warning {
                            color: #faad14;
                        }
                        
                        .info-icon.info {
                            color: #1890ff;
                        }
                        
                        .info-icon.success {
                            color: #52c41a;
                        }
                        
                        .cost-summary {
                            background-color: #f8f9fa;
                            border: 1px solid #e9ecef;
                        }
                        
                        .cost-breakdown .cost-line {
                            margin-bottom: 8px;
                        }
                        
                        .cost-breakdown .total-cost {
                            background-color: #e6f7ff;
                            padding: 8px;
                            border-radius: 4px;
                            margin-top: 8px;
                        }
                        
                        .bg-light {
                            background-color: #f5f5f5;
                        }
                        
                        .mr-2 { margin-right: 8px; }
                        .me-2 { margin-right: 8px; }
                        .mb-3 { margin-bottom: 12px; }
                        .mb-4 { margin-bottom: 16px; }
                        .my-2 { margin: 8px 0; }
                        .text-lg { font-size: 16px; }
                        .text-blue-600 { color: #1890ff; }
                    `}
                </style>
            </SDIModal>
            {/* Modal pour le conducteur habituel */}
            <SDIModal
                title={`Conducteur habituel - Engin N°${modalConducteur.enginIndex !== null ? modalConducteur.enginIndex + 1 : ''}`}
                open={modalConducteur.show}
                onOk={saveConducteurHabituel}
                onCancel={() => setModalConducteur(prev => ({ ...prev, show: false }))}
                width={800}
                okText="Enregistrer"
                cancelText="Annuler"
                zIndex={2000}
            >
                <ConfigProvider locale={frFR}>
                    <div className="text-center mb-4">
                        <Text type="secondary">
                            Précisez le conducteur habituel si différent du souscripteur du contrat
                        </Text>
                    </div>

                    <Form layout="vertical">
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item label="Nom *">
                                    <Input
                                        name="nom"
                                        value={modalConducteur.data.nom}
                                        onChange={handleConducteurChange}
                                        placeholder="Nom du conducteur"
                                    />
                                </Form.Item>
                            </Col>

                            <Col span={12}>
                                <Form.Item label="Prénom(s) *">
                                    <Input
                                        name="prenom"
                                        value={modalConducteur.data.prenom}
                                        onChange={handleConducteurChange}
                                        placeholder="Prénom(s) du conducteur"
                                    />
                                </Form.Item>
                            </Col>

                            <Col span={12}>
                                <Form.Item label="Sexe *">
                                    <Select
                                        name="genre"
                                        value={modalConducteur.data.genre}
                                        onChange={(value) => handleConducteurChange({ target: { name: 'genre', value } })}
                                    >
                                        <Option value="MASCULIN">MASCULIN</Option>
                                        <Option value="FEMININ">FEMININ</Option>
                                    </Select>
                                </Form.Item>
                            </Col>

                            <Col span={12}>
                                <Form.Item label="Profession">
                                    <Select
                                        name="profession"
                                        value={modalConducteur.data.profession}
                                        onChange={(value) => handleConducteurChange({ target: { name: 'profession', value } })}
                                        placeholder="Sélectionner la profession"
                                        showSearch
                                    >
                                        <Option value="">Aucune</Option>
                                        {professions.map((prof, id) => (
                                            <Option key={id} value={prof.libelleParametre}>
                                                {prof.libelleParametre}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>

                            <Col span={12}>
                                <Form.Item label="Téléphone *">
                                    <Input
                                        name="telephone"
                                        value={modalConducteur.data.telephone}
                                        onChange={handleConducteurChange}
                                        placeholder="00000000"
                                        addonBefore="TG (+228)"
                                    />
                                </Form.Item>
                            </Col>

                            <Col span={12}>
                                <Form.Item label="Email">
                                    <Input
                                        name="email"
                                        type="email"
                                        value={modalConducteur.data.email}
                                        onChange={handleConducteurChange}
                                        placeholder="email@exemple.com"
                                    />
                                </Form.Item>
                            </Col>

                            <Col span={12}>
                                <Form.Item label="Adresse *">
                                    <Input
                                        name="adresse"
                                        value={modalConducteur.data.adresse}
                                        onChange={handleConducteurChange}
                                        placeholder="Adresse de résidence"
                                    />
                                </Form.Item>
                            </Col>

                            <Col span={12}>
                                <Form.Item label="Type de pièce">
                                    <Select
                                        name="typePiece"
                                        value={modalConducteur.data.typePiece}
                                        onChange={(value) => handleConducteurChange({ target: { name: 'typePiece', value } })}
                                        placeholder="Sélectionner le type"
                                    >
                                        <Option value="">Aucune</Option>
                                        <Option value="CNI">CNI</Option>
                                        <Option value="PASSEPORT">PASSEPORT</Option>
                                        <Option value="PERMIS">PERMIS DE CONDUIRE</Option>
                                        <Option value="ELECTEUR">CARTE D'ÉLECTEUR</Option>
                                    </Select>
                                </Form.Item>
                            </Col>

                            <Col span={12}>
                                <Form.Item label="Numéro de la pièce">
                                    <Input
                                        name="numIdentite"
                                        value={modalConducteur.data.numIdentite}
                                        onChange={handleConducteurChange}
                                        placeholder="Numéro de la pièce"
                                    />
                                </Form.Item>
                            </Col>

                            <Col span={12}>
                                <Form.Item label="Date de délivrance">
                                    <Input
                                        type="date"
                                        name="dateDelivrance"
                                        value={modalConducteur.data.dateDelivrance}
                                        onChange={handleConducteurChange}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>
                </ConfigProvider>
            </SDIModal>
        </>
    );
}

Incorporation.propTypes = {
    open: PropTypes.bool,
    update: PropTypes.func,
    contrat: PropTypes.object
};

export default Incorporation;
